import { useRef, type ReactNode, type RefObject } from 'react';
import {
  useSelect,
  usePopover,
  useButton,
  HiddenSelect,
  Overlay,
  DismissButton,
  type AriaSelectProps,
  type AriaPopoverProps,
  type AriaButtonProps,
} from 'react-aria';
import { useSelectState, type OverlayTriggerState } from 'react-stately';
import { useMeasure } from 'react-use';
import { CaretDownNew, Warning } from '@hungryroot/icons';
import { Dimension } from '../../main';
import { Paragraph } from '../../typography/Paragraph/Paragraph';
import { InputDetail } from '../InputDetail/InputDetail';
import { SelectListBox } from '../SelectListBox/SelectListBox';

import styles from './Select.module.css';

interface SelectProps<T> extends AriaSelectProps<T> {
  testid?: string;
  errorMessage?: string | string[];
  helpText?: string;
  className?: string;
}

interface PopoverProps extends AriaPopoverProps {
  testid?: string;
  children: ReactNode;
  state: OverlayTriggerState;
  style: object;
  className?: string;
}

interface ButtonProps extends AriaButtonProps {
  testid?: string;
  buttonRef: RefObject<HTMLButtonElement>;
  className: string;
}

export function Select<T extends object>(props: SelectProps<T>) {
  const {
    testid,
    isDisabled,
    name,
    errorMessage,
    helpText,
    placeholder,
    className,
  } = props;
  const state = useSelectState(props);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { labelProps, triggerProps, valueProps, menuProps } = useSelect(
    props,
    state,
    triggerRef
  );

  const [containerRef, { width: containerWidth }] =
    useMeasure<HTMLDivElement>();

  function getInputText() {
    if (state.selectedItem) {
      return (
        <Paragraph className={styles.selected}>
          {state.selectedItem.rendered}
        </Paragraph>
      );
    }
    if (placeholder) {
      return (
        <Paragraph className={styles.placeholder}>{placeholder}</Paragraph>
      );
    }
    if (state.isOpen) {
      return (
        <Paragraph className={styles['placeholder-fallback']}>Select</Paragraph>
      );
    }
  }

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className={`${styles.container} ${
          isDisabled ? styles.disabled : undefined
        } ${errorMessage ? styles['is-error'] : undefined}`}
      >
        <HiddenSelect
          isDisabled={isDisabled}
          state={state}
          triggerRef={triggerRef}
          label={props.label}
          name={name}
        />
        <Select.Button
          {...triggerProps}
          testid={testid}
          buttonRef={triggerRef}
          className={`${styles.trigger} ${
            state.isOpen ? styles['is-active'] : undefined
          } ${state.selectedItem ? styles['is-filled'] : undefined}`}
        >
          <span {...valueProps}>{getInputText()}</span>
          <div>
            {errorMessage && (
              <span className={styles['error-icon']}>
                <Warning />
              </span>
            )}
            <span aria-hidden="true" className={styles.icon}>
              <CaretDownNew />
            </span>
          </div>
        </Select.Button>
        <div
          {...labelProps}
          data-testid={`${testid}-label`}
          className={styles.label}
        >
          {props.label}
        </div>
        {state.isOpen && (
          <Select.Popover
            testid={testid}
            state={state}
            triggerRef={triggerRef}
            popoverRef={popoverRef}
            placement="bottom start"
            style={{
              width: `${containerWidth}px`,
            }}
            className={
              errorMessage || helpText
                ? styles['popover-with-details']
                : undefined
            }
          >
            <SelectListBox {...menuProps} state={state} testid={testid} />
          </Select.Popover>
        )}
      </div>
      <InputDetail errorMessage={errorMessage} helpText={helpText} />
    </div>
  );
}

function SelectPopover(props: PopoverProps) {
  const { testid, state, children, className, popoverRef } = props;
  const { popoverProps, underlayProps } = usePopover(
    {
      ...props,
      popoverRef,
    },
    state
  );

  return (
    <Overlay>
      <div {...underlayProps} />
      <Dimension
        level="three"
        {...popoverProps}
        data-testid={`${testid}-popover`}
        ref={popoverRef as RefObject<HTMLDivElement>}
        className={`${styles.popover} ${className}`}
        style={{
          ...popoverProps.style,
          ...props.style,
        }}
      >
        <DismissButton onDismiss={state.close} />
        {children}
        <DismissButton onDismiss={state.close} />
      </Dimension>
    </Overlay>
  );
}

Select.Popover = SelectPopover;

function SelectButton(props: ButtonProps) {
  const { testid, buttonRef, className } = props;
  const { buttonProps } = useButton(props, buttonRef);
  return (
    <button
      data-testid={`${testid}-trigger`}
      {...buttonProps}
      ref={buttonRef}
      className={className}
    >
      {props.children}
    </button>
  );
}

Select.Button = SelectButton;
